import { useState } from "react";
import { Box, Button, Dialog, FormControl, InputLabel, MenuItem, TextField } from "@mui/material";
import { Cell, RenameSelectOptionAndMigrateDocument } from "generated/graphql";
import { P } from "theme/typography";
import { LoadingButton } from "@mui/lab";
import { Close } from "@mui/icons-material";
import { useAppSelector } from "redux/hooks";
import { selectLensEditorFormId } from "redux/slices/lensEditor";
import RequestStatus from "redux/slices/RequestStatus";
import PSelect from "components/PSelect";
import { useSnackbar } from "notistack";
import { runGqlMutation } from "gqlClient";

type SelectOptionDialogProps = {
  onClose: () => void;
  cell: Cell;
  onSuccess: () => void;
};

const SelectOptionDialog = ({ onClose, cell, onSuccess }: SelectOptionDialogProps) => {
  const [status, setStatus] = useState<RequestStatus>(RequestStatus.IDLE);
  const formId = useAppSelector(selectLensEditorFormId);
  const { enqueueSnackbar } = useSnackbar();
  const [oldValue, setOldValue] = useState<string>("");
  const [newValue, setNewValue] = useState<string>("");

  const startMigration = (cellId: number, formId: number, currentValue: string, newValue: string) => {
    setStatus(RequestStatus.PENDING);
    runGqlMutation(RenameSelectOptionAndMigrateDocument, {
      cellId,
      formId,
      currentValue,
      newValue,
    })
      .then((resp) => {
        setStatus(RequestStatus.SUCCEEDED);
        const count = resp.lens.form.renameSelectOptionAndMigrate;
        enqueueSnackbar(`Option '${oldValue}' successfully renamed to '${newValue}'. ${count} responses updated`, {
          variant: "success",
        });
        onSuccess();
      })
      .catch(() => {
        setStatus(RequestStatus.FAILED);
        enqueueSnackbar(`Operation failed`, {
          variant: "error",
        });
      });
  };

  return (
    <Dialog
      open={true}
      onClose={() => {
        onClose();
      }}>
      <Box
        sx={{
          padding: 2,
          width: 500,
          "& > * + *": {
            marginTop: 1,
          },
        }}>
        <Box
          sx={{
            marginBottom: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}>
          <Box
            sx={{
              "& > * + *": {
                marginTop: 1,
              },
            }}>
            <P variant="xlBold">Rename Existing Select Option</P>
            <br />
            <P variant="xs">Select an Option to rename</P>
          </Box>
          <Box>
            <Button
              color="secondary"
              onClick={() => {
                onClose();
              }}>
              <Close />
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            "& > * + *": {
              marginTop: 2,
            },
          }}>
          <FormControl fullWidth disabled={status === RequestStatus.PENDING} size="small">
            <InputLabel>Old Value</InputLabel>
            <PSelect
              value={oldValue}
              onChange={(e) => {
                setOldValue(e.target.value as string);
                // help the user by filling in the new value with the old value
                setNewValue(e.target.value as string);
              }}>
              {cell.selectOptions &&
                cell.selectOptions.map((option, idx) => {
                  return (
                    <MenuItem key={idx} value={option}>
                      {option}
                    </MenuItem>
                  );
                })}
            </PSelect>
          </FormControl>

          <TextField
            label="New value"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onFocus={(e) => e.target.select()}
            fullWidth
            disabled={status === RequestStatus.PENDING}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}>
          <LoadingButton
            variant="contained"
            color="primary"
            loading={status === RequestStatus.PENDING}
            disabled={status === RequestStatus.PENDING || newValue === "" || oldValue === ""}
            onClick={() => {
              startMigration(cell.id, formId, oldValue, newValue);
            }}>
            Rename and Migrate Existing Responses
          </LoadingButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default SelectOptionDialog;
